import { speaker } from "assets/website";

export interface LearningCard {
  number: string;
  title: string;
  description: string;
  bgColor: string;
}

export interface ProblemParagraph {
  segments: { text: string; bold?: boolean }[];
}

export interface EventData {
  slug: string;
  badge: string;
  title: string;
  subtitle: string;
  date: string;
  time: string;
  venue: string;
  entry: string;
  entryNote?: string;
  lumaUrl: string;
  problem: {
    headingLead: string;
    headingHighlight: string;
    paragraphs: ProblemParagraph[];
  };
  learnings: LearningCard[];
  speaker: {
    name: string;
    role: string;
    image: string;
    bio: string;
  };
  finalCta: {
    title: string;
    body: string;
  };
}

export const events: EventData[] = [
  {
    slug: "bridging-the-gap-between-talent-and-performance",
    badge: "Executive Roundtable · PrecisionMatch",
    title: "Bridging the Gap Between Talent and Performance",
    subtitle:
      "In this executive learning event we'll be exploring why high-potential candidates don't always translate into high-performing employees and how to fix it before they walk in the door.",
    date: "Thursday, April 30",
    time: "1 – 2 PM GMT+1",
    venue: "Virtual",
    entry: "Free",
    entryNote: "Registration required",
    lumaUrl: "https://luma.com/event/evt-wFEGN9Ef4UKtQov",
    problem: {
      headingLead: "The team isn't the problem.",
      headingHighlight: "The front door is.",
      paragraphs: [
        {
          segments: [
            {
              text: "You've been here before. You hired someone impressive great in the interview, strong CV, confident references. Three months in, you're managing performance issues you didn't see coming. Six months in, you're questioning the hire entirely.",
            },
          ],
        },
        {
          segments: [
            { text: "This isn't a management failure. It isn't a training gap. " },
            {
              text: "It's a hiring problem that started the moment impressions were mistaken for execution ability.",
              bold: true,
            },
          ],
        },
        {
          segments: [
            {
              text: "For too long, organizations have been evaluating candidates on how they present not on how they actually work. Interviews measure confidence, CVs measure career marketing. Neither measures the thing that actually matters: ",
            },
            { text: "can this person execute?", bold: true },
          ],
        },
        {
          segments: [
            {
              text: "This roundtable is a focused, honest conversation about the gap where it comes from, what it costs, and what hiring looks like when you close it.",
              bold: true,
            },
          ],
        },
      ],
    },
    learnings: [
      {
        number: "1.",
        title: "Why your management problems are actually hiring problems",
        description:
          "We'll trace the performance issues that look like management failures back to where they actually started the hiring decision.",
        bgColor: "bg-[#FFF2F6]",
      },
      {
        number: "2.",
        title: "The Mismatch Tax",
        description:
          "Beyond salary, we'll explore the actual cost of a bad hiring.",
        bgColor: "bg-[#E4FAFF]",
      },
      {
        number: "3.",
        title: "Screening for execution",
        description:
          "What outcome-based evaluation actually looks like. How to move from impression-driven to evidence-driven hiring.",
        bgColor: "bg-[#EEEEFF]",
      },
      {
        number: "4.",
        title: "Precisionmatch in action",
        description:
          "A walkthrough of how Precisionmatch validates execution competence before a candidate reaches your interview.",
        bgColor: "bg-[#FDF3E2]",
      },
      {
        number: "5.",
        title: "Open Q&A bring your specific situation",
        description: "We'll spend the final segment on your questions.",
        bgColor: "bg-[#F2F4F7]",
      },
    ],
    speaker: {
      name: "Kemi Ogunmuyiwa",
      role: "Talent Enablement Partner",
      image: speaker,
      bio: "Kemi Ogunmuyiwa is a People & Talent Leader with expertise in building people-centric cultures and driving organizational growth. With an MBA and SPHRi™ certification, she specializes in aligning people strategy with business goals, developing leaders, and strengthening team performance. She actively shares insights on talent, culture, and career growth, championing clarity, intentional work, and meaningful relationships to enable sustainable impact and thriving workplaces.",
    },
    finalCta: {
      title: "Hire and Retain the Right People on your Team",
      body: "Precisionmatch is a talent assessment tool that acts as a hiring and retention filter validating real competence and execution ability so teams can hire people who actually get work done and drive stronger returns on salary investment.",
    },
  },
];

export const getEventBySlug = (slug: string) =>
  events.find((event) => event.slug === slug);
